import React, { useEffect, useCallback } from "react";
import { pannersAtom, userLocationsAtom } from "../jotai/atoms";
import { styled } from "../stitches.config";
import { AnyBlock, Point2D, User } from "../types";
import { useAtom } from "jotai";
import DefaultCursor from "./DefaultCursor";
import { motion, MotionValue, useMotionValue } from "framer-motion";
import Stream from "./Stream";
import {
  DEFAULT_CURSOR_OFFSET,
  DEFAULT_CURSOR_SIZE,
  DEFAULT_SPRING,
  ROOM_SERVICE_MAP_KEYS,
  ROOM_SERVICE_PRESENCE_KEYS,
} from "../consts";
import { setPannerLocation } from "./PositionalAudio";
import Icon from "../components/Icon";
import { usePresenceCallback } from "../roomservice/utils";
import { useMapWrapper, useSpaceInfo } from "../roomservice/maps";

// REFACTOR: a lot of `isSelf` checks are happening here, maybe we should have separate SelfCursor and Cursor?
export default function Cursor({
  isSelf,
  stream,
  userId,
  currentUserPos,
  isStreaming,
  isMuted,
  upsertBlock,
}: {
  isSelf: boolean;
  stream?: any;
  userId: string;
  currentUserPos: Record<string, MotionValue<number>>;
  isStreaming: boolean;
  isMuted: boolean;
  upsertBlock: (block: AnyBlock) => void;
}) {
  const [userInfo] = useMapWrapper<User>(ROOM_SERVICE_MAP_KEYS.USER + userId);
  const [userLocations] = useAtom(userLocationsAtom);
  const userLocation = userLocations[userId];
  const [panners] = useAtom(pannersAtom);
  const [spaceInfo] = useSpaceInfo();
  const cursorSize = spaceInfo?.cursorSize || DEFAULT_CURSOR_SIZE;

  const x = useMotionValue(0);
  const y = useMotionValue(0);

  const onPositionPresence = useCallback(
    (msg: Record<string, Point2D>) => {
      if (!msg[userId]) {
        return;
      }

      x.set(msg[userId].x);
      y.set(msg[userId].y);
    },
    [x, y, userId]
  );

  usePresenceCallback(ROOM_SERVICE_PRESENCE_KEYS.POSITION, onPositionPresence);

  useEffect(
    () =>
      x.onChange(() => setPannerLocation(panners[userId], x.get(), y.get())),
    [panners, userId, x, y]
  );

  useEffect(
    () =>
      y.onChange(() => setPannerLocation(panners[userId], x.get(), y.get())),
    [panners, userId, x, y]
  );

  if (!userInfo) return <></>;

  const position = {
    x: isSelf ? currentUserPos.x : x,
    y: isSelf ? currentUserPos.y : y,
  };

  const isElsewhere = userLocation && userLocation.block;
  const isStreamDocked = isStreaming && !isElsewhere;
  const isStreamNearCursor = stream && isStreamDocked;

  return (
    <div style={{ pointerEvents: "none" }} className="cursor">
      {stream ? (
        <Stream
          userId={userId}
          stream={stream}
          isStreamingVideo={isStreaming}
          isSelf={isSelf}
          location={userLocation}
          mousePos={position}
          color={userInfo.color}
          upsertBlock={upsertBlock}
        />
      ) : null}
      <CursorContainer
        style={{
          ...position,
          width: cursorSize + DEFAULT_CURSOR_OFFSET * 2,
          height: cursorSize + DEFAULT_CURSOR_OFFSET * 2,
        }}
      >
        <NameTag
          className={`nametag ${userInfo.name}`}
          style={{
            backgroundColor: userInfo.color,
          }}
          animate={{
            top: isStreamNearCursor ? cursorSize : "1rem",
            left: isStreamNearCursor ? "auto" : "1rem",
          }}
          transition={DEFAULT_SPRING}
        >
          <NameCaption>
            {userInfo.name}
            {isMuted && <MuteIcon icon="MicOff" />}
          </NameCaption>
        </NameTag>
        <DefaultCursor color={userInfo.color} />
      </CursorContainer>
    </div>
  );
}

const CursorContainer = styled(motion.figure, {
  position: "absolute",
  display: "grid",
  justifyItems: "center",
  zIndex: "$cursor",
  lineHeight: "$4",
});

const NameCaption = styled("figcaption", {
  display: "grid",
  gridAutoFlow: "column",
  gap: "$1",
  placeItems: "center",
});

export const NameTag = styled(motion.div, {
  position: "absolute",
  background: "$transparentBlack",
  boxShadow: "$cursor",
  border: "2px solid $transparentWhite",
  fontSize: "$4",
  borderRadius: "$2",
  color: "$brown",
  padding: "$1 $2",
  textAlign: "center",
  whiteSpace: "nowrap",
  "&::before": {
    content: `""`,
    display: "block",
    position: "absolute",
    top: "0",
    bottom: "0",
    right: "0",
    left: "0",
    borderRadius: "calc(.5rem - 2px)",
    border: "1px solid $brown",
  },
});

export const MuteIcon = styled(Icon, {
  marginRight: "-$1",
});
